import { getDownloadURL, ref, uploadBytesResumable, type UploadTaskSnapshot } from "firebase/storage";
import { storage } from "./firebase";
import type { ShareableContent, ShareChannel } from "./types";

export const SHAREABLE_VIDEO_MAX_BYTES = 70 * 1024 * 1024;

const videoExtensions: Record<string, string> = {
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm"
};

function safeFileName(value: string) {
  const cleaned = value
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\.{2,}/g, ".")
    .replace(/\s+/g, " ")
    .replace(/^[. ]+|[. ]+$/g, "")
    .trim();
  return cleaned.slice(0, 120) || "video";
}

function greatestCommonDivisor(left: number, right: number) {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

function readVideoMetadata(file: File) {
  return new Promise<{
    aspectRatio: string;
    duration?: number;
    height: number;
    width: number;
  }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("No fue posible leer los metadatos del video."));
    }, 10000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    };

    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) {
        cleanup();
        reject(new Error("No fue posible leer las dimensiones del video."));
        return;
      }
      const divisor = greatestCommonDivisor(width, height);
      const metadata = {
        aspectRatio: `${width / divisor}:${height / divisor}`,
        ...(Number.isFinite(video.duration) && video.duration > 0 ? { duration: video.duration } : {}),
        height,
        width
      };
      cleanup();
      resolve(metadata);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("No fue posible leer los metadatos del video."));
    };
    video.src = url;
  });
}

export async function uploadShareableVideo({
  adminUid,
  channel,
  contentId,
  file
}: {
  adminUid: string;
  channel: ShareChannel;
  contentId: string;
  file: File;
}): Promise<ShareableContent["assets"][number]> {
  const extension = videoExtensions[file.type];
  if (!extension) throw new Error("Usa un video MP4, WebM o MOV.");
  if (!file.size) throw new Error("El video esta vacio.");
  if (file.size > SHAREABLE_VIDEO_MAX_BYTES) throw new Error("El video debe pesar maximo 70 MB.");

  const metadataPromise = readVideoMetadata(file).catch(() => null);
  const id = crypto.randomUUID();
  const label = safeFileName(file.name);
  const storagePath = `shareable-content/${contentId}/full/${Date.now()}-${id}.${extension}`;
  const uploadRef = ref(storage, storagePath);
  const task = uploadBytesResumable(uploadRef, file, {
    cacheControl: "public, max-age=31536000, immutable",
    contentType: file.type,
    customMetadata: {
      adminUid,
      originalFileName: label,
      purpose: "shareable-content-full-video"
    }
  });
  const snapshot = await new Promise<UploadTaskSnapshot>((resolve, reject) => {
    task.on("state_changed", undefined, reject, () => resolve(task.snapshot));
  });
  const url = await getDownloadURL(snapshot.ref);
  const metadata = await metadataPromise;

  return {
    ...(metadata || {}),
    channel,
    fullMimeType: file.type,
    fullSize: file.size,
    fullStoragePath: storagePath,
    fullUrl: url,
    id,
    label,
    mimeType: file.type,
    size: file.size,
    storagePath,
    type: "video",
    url
  };
}
